import { cn } from '@/lib/utils';

export interface AutomationRateBarProps {
  rate: number;
  exactCount?: number;
  suggestedCount?: number;
  generatedCount?: number;
  noMatchCount?: number;
  showBreakdown?: boolean;
  className?: string;
}

export function AutomationRateBar({
  rate,
  exactCount = 0,
  suggestedCount = 0,
  generatedCount = 0,
  noMatchCount = 0,
  showBreakdown = false,
  className,
}: AutomationRateBarProps) {
  const total = exactCount + suggestedCount + generatedCount + noMatchCount;
  const hasBreakdown = total > 0;

  const getPercentage = (count: number) =>
    total > 0 ? (count / total) * 100 : 0;

  const exactPct = getPercentage(exactCount);
  const suggestedPct = getPercentage(suggestedCount);
  const generatedPct = getPercentage(generatedCount);
  const noMatchPct = getPercentage(noMatchCount);

  const tooltipText = hasBreakdown
    ? `Exact: ${exactCount}, Suggested: ${suggestedCount}, Generated: ${generatedCount}, No Match: ${noMatchCount}`
    : `${rate.toFixed(1)}% automation rate`;

  return (
    <div className={cn('space-y-1', className)}>
      <div
        className="h-2 w-full bg-muted rounded-full overflow-hidden flex cursor-help"
        title={tooltipText}
      >
        {hasBreakdown ? (
          <>
            {exactPct > 0 && (
              <div
                className="h-full bg-tier-exact transition-all"
                style={{ width: `${exactPct}%` }}
              />
            )}
            {suggestedPct > 0 && (
              <div
                className="h-full bg-tier-suggested transition-all"
                style={{ width: `${suggestedPct}%` }}
              />
            )}
            {generatedPct > 0 && (
              <div
                className="h-full bg-tier-generated transition-all"
                style={{ width: `${generatedPct}%` }}
              />
            )}
            {noMatchPct > 0 && (
              <div
                className="h-full bg-tier-nomatch transition-all"
                style={{ width: `${noMatchPct}%` }}
              />
            )}
          </>
        ) : (
          <div
            className={cn(
              'h-full transition-all',
              rate >= 80 ? 'bg-green-500' : rate >= 50 ? 'bg-amber-500' : 'bg-red-500'
            )}
            style={{ width: `${rate}%` }}
          />
        )}
      </div>
      {showBreakdown && hasBreakdown && (
        <div className="flex gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-tier-exact" />
            {exactCount}
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-tier-suggested" />
            {suggestedCount}
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-tier-generated" />
            {generatedCount}
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-tier-nomatch" />
            {noMatchCount}
          </span>
        </div>
      )}
    </div>
  );
}
